<script setup lang="ts" generic="T extends Record<string, string>">
/**
 * A column whose value comes from a fixed option list — the enum column of every
 * grid. Its filter, create and edit cell all mount the plain `NxSelectInput`, and
 * that is a decision, not an oversight (I-1016): the typed `NxFilterSelectInput`
 * the masks use for enums was measured against this context and left out of it.
 * The lists here are ten values at most and three or four typically, so there is
 * nothing to narrow; `NxSelectInput`'s typeahead already reaches an option
 * without a text field; and the columns that matter render their options as
 * MARKUP through `#option` (status dot, pill, badge, meter), which a text-input
 * trigger cannot show at all. Long unbounded lists in a grid are relation
 * columns, typable in their cells since long before this question was asked.
 *
 * What this column does owe those markup options is their plain-text twin —
 * see `optionText` below.
 */
import { computed, ref } from 'vue';
import { default as NxTableColumn, type NxTableColumnProps } from './NxTableColumn.vue';
import { NxSelectInput } from '../Inputs';
import { defaultEditCellFocusRef, onEditCellKeydown, PartialOrNull } from './internal';

const props = withDefaults(
  defineProps<
    Omit<NxTableColumnProps<T, keyof T, string>, 'toCsvString'> & {
      /** selectable options the user can choose from */
      options: string[];
      /** value that will be used for the csv export */
      toCsvString?: NxTableColumnProps<T, keyof T, string>['toCsvString'];
      /**
       * Plain-text twin of an option, handed to the three cell inputs. Whenever the
       * `#option` slot renders something other than the raw value — a translated
       * enum label, a pill, a meter — their typeahead, tooltip and filtering match
       * text no reader of this column ever sees (`partial_delivery` for what says
       * "Teillieferung"). That is what the dev warning in `useSelectSelection`
       * points at; there was no way to answer it from here before (I-1016).
       */
      optionText?: (option: string) => string;
    }
  >(),
  {
    filterable: true,
    resizeable: true,
    reorderable: true,
    sortable: true,
    editable: true,
    defaultVisible: true,
    layout: 'row',
    toCsvString: undefined,
    optionText: undefined,
  }
);

/**
 * One function, two channels: the cell inputs ask this component for their
 * option text, the filter popover asks the column layer for its labels. A column
 * that states its words once — through either prop — must not have to say them
 * twice, or its dropdowns show "Teillieferung" while the filter checklist and
 * its chips show `partial_delivery`. A column whose two channels genuinely read
 * differently states BOTH; each prop wins in its own channel.
 */
const optionText_ = computed(() => props.optionText ?? props.filterOptionLabel);

/** A cell that holds nothing — no key, no value, the empty string. */
function isEmptyCell(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

/**
 * The display text of a cell, which is also its CSV value. The words must not
 * stop at the input cells: a column that states them once and writes no `#cell`
 * slot of its own would otherwise read `partial_delivery` in the body while its
 * dropdown, tooltip and filter checklist all say "Teillieferung". An explicit
 * `toCsvString` still wins — a column that formats its own text said so.
 */
function toCsvString_(rowData: T, rowIndex: number) {
  if (props.toCsvString) {
    return props.toCsvString(rowData, rowIndex);
  }
  const value = rowData[props.columnId];
  const text = optionText_.value;
  return text && !isEmptyCell(value) ? text(value as string) : value;
}

/**
 * The cell value as the select's model. `[null]` is the wire form of "nothing
 * selected"; wrapping an empty cell in a one-element array makes it a SELECTION
 * instead, and the `#option` slot then renders an option that does not exist —
 * a meter at 0% for a step nobody recorded, where the display cell of the same
 * column deliberately draws nothing.
 */
function editModelOf(rowData: T): string[] | [null] {
  const value = rowData[props.columnId];
  return isEmptyCell(value) ? [null] : [value as string];
}

const filterValue = ref<string[]>([]);
const createValue = ref<string[]>([]);
function onQueryLoaded(value: string[]) {
  filterValue.value = value;
}
</script>

<template>
  <!-- @vue-generic {T, keyof T, string} -->
  <NxTableColumn
    v-bind="{
      ...props,
      filterKind: props.filterKind ?? 'select',
      filterOptions: props.filterOptions ?? props.options,
      filterOptionLabel: props.filterOptionLabel ?? props.optionText,
      onQueryLoaded: props.onQueryLoaded ? props.onQueryLoaded : onQueryLoaded,
      toCsvString: toCsvString_,
    }"
  >
    <template v-if="$slots.summaryCell" #summaryCell="summaryProps">
      <slot name="summaryCell" v-bind="summaryProps"></slot>
    </template>
    <template v-if="$slots.headerCell" #headerCell>
      <slot name="headerCell"></slot>
    </template>
    <template
      #filterCell="{
        setFilterValue,
        disabled,
        registerFocus,
        focusAbove,
        focusBelow,
        focusLeft,
        focusRight,
      }"
    >
      <slot
        name="filterCell"
        v-bind="{
          setFilterValue,
          disabled,
          registerFocus,
          focusAbove,
          focusBelow,
          focusLeft,
          focusRight,
        }"
      >
        <NxSelectInput
          arrow-keys-navigate
          :ref="defaultEditCellFocusRef(registerFocus)"
          v-model="filterValue"
          height="xs"
          placeholder=""
          :option-text="optionText_"
          :disabled="disabled"
          :options="props.options"
          @update:model-value="
            (value: string[] | [null]) => {
              setFilterValue(value.at(0) ? (value as string[]) : []);
            }
          "
          @keydown="
            (e: KeyboardEvent) =>
              onEditCellKeydown(e, {
                focusAbove,
                focusBelow,
                focusLeft,
                focusRight,
                inputType: 'select',
              })
          "
        >
          <template v-if="$slots.placeholder" #placeholder="{ placeholder }">
            <slot name="placeholder" :placeholder="placeholder"></slot>
          </template>
          <template v-if="$slots.option" #option="{ option }">
            <slot name="option" :option="option"></slot>
          </template>
        </NxSelectInput>
      </slot>
    </template>
    <template #cell="{ rowData, rowIndex }">
      <slot name="cell" v-bind="{ rowData, rowIndex }">
        <span class="one-line">{{ toCsvString_(rowData, rowIndex) }}</span>
      </slot>
    </template>
    <template #createCell="{ setPartialRowData }">
      <slot name="createCell" v-bind="{ setPartialRowData }">
        <NxSelectInput
          arrow-keys-navigate
          v-model="createValue"
          :placeholder="`${props.displayName ?? (props.columnId as string)}`"
          :options="props.options"
          :option-text="optionText_"
          @update:model-value="
            (value: string[] | [null]) => {
              setPartialRowData({
                [props.columnId]: value[0],
              } as PartialOrNull<T>);
            }
          "
        >
          <template v-if="$slots.placeholder" #placeholder="{ placeholder }">
            <slot name="placeholder" :placeholder="placeholder"></slot>
          </template>
          <template v-if="$slots.option" #option="{ option }">
            <slot name="option" :option="option"></slot>
          </template>
        </NxSelectInput>
      </slot>
    </template>
    <template
      #editCell="{
        rowData,
        rowIndex,
        onEdit,
        registerFocus,
        focusAbove,
        focusBelow,
        focusLeft,
        focusRight,
      }"
    >
      <slot
        name="editCell"
        v-bind="{
          rowData,
          rowIndex,
          onEdit,
          registerFocus,
          focusAbove,
          focusBelow,
          focusLeft,
          focusRight,
        }"
      >
        <div class="nx-ghost-cell">
          <NxSelectInput
            arrow-keys-navigate
            :ref="defaultEditCellFocusRef(registerFocus)"
            :model-value="editModelOf(rowData)"
            :placeholder="`${props.displayName ?? (props.columnId as string)}`"
            :options="props.options"
            :option-text="optionText_"
            @update:model-value="
              (value: string[] | [null]) => {
                onEdit({ [props.columnId]: value[0] } as Partial<T>);
              }
            "
            @keydown="
              (e: KeyboardEvent) =>
                onEditCellKeydown(e, {
                  focusAbove,
                  focusBelow,
                  focusLeft,
                  focusRight,
                  inputType: 'select',
                })
            "
          >
            <template v-if="$slots.placeholder" #placeholder="{ placeholder }">
              <slot name="placeholder" :placeholder="placeholder"></slot>
            </template>
            <template v-if="$slots.option" #option="{ option }">
              <slot name="option" :option="option"></slot>
            </template>
          </NxSelectInput>
        </div>
      </slot>
    </template>
  </NxTableColumn>
</template>

<style scoped>
.one-line {
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  /* Cut long values with an ellipsis, never mid-letter. */
  text-overflow: ellipsis;
}
</style>
